import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Star, Send, Loader2, CheckCircle, MessageSquare, Sparkles, Reply
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ value, onChange, size = 24, readonly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="sf-stars-row" data-testid="stars-row">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            className={`sf-star-btn ${filled ? 'filled' : ''} ${readonly ? 'readonly' : ''}`}
            onClick={() => !readonly && onChange && onChange(n)}
            onMouseEnter={() => !readonly && setHover(n)}
            onMouseLeave={() => !readonly && setHover(0)}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            disabled={readonly}
            data-testid={`star-${n}`}
          >
            <Star size={size} fill={filled ? 'currentColor' : 'none'} />
          </button>
        );
      })}
    </div>
  );
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const ReviewCard = ({ review, isAdmin, onReplyPosted }) => {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(null);

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    setErr(null);
    try {
      const token = localStorage.getItem('sf_admin_token');
      await axios.post(`${API}/reviews/${review.id}/reply`, {
        admin_token: token,
        reply: replyText.trim(),
      });
      onReplyPosted();
      setReplyOpen(false);
      setReplyText('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not post reply.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="sf-review-card" data-testid={`review-${review.id}`}>
      <div className="sf-review-header">
        <div className="sf-review-author">
          <div className="sf-review-avatar">{review.name.charAt(0).toUpperCase()}</div>
          <div>
            <div className="sf-review-name">{review.name}</div>
            <div className="sf-review-date">{formatDate(review.created_at)}</div>
          </div>
        </div>
        <StarRow value={review.stars} readonly size={18} />
      </div>
      <p className="sf-review-comment">{review.comment}</p>

      {/* Founder reply */}
      {review.reply && (
        <div className="sf-review-reply">
          <div className="sf-reply-header">
            <Sparkles size={14} />
            <span className="sf-reply-name">Aaron · Scribe Flow Studio</span>
            <span className="sf-reply-date">{formatDate(review.replied_at)}</span>
          </div>
          <p className="sf-reply-text">{review.reply}</p>
        </div>
      )}

      {/* Admin reply UI */}
      {isAdmin && !review.reply && (
        <div className="sf-admin-reply-wrap">
          {!replyOpen ? (
            <button className="sf-admin-reply-btn" onClick={() => setReplyOpen(true)}>
              <Reply size={14} /> Reply as Founder
            </button>
          ) : (
            <div className="sf-admin-reply-form">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a thoughtful reply..."
                className="sf-admin-reply-textarea"
              />
              {err && <p className="sf-admin-reply-err">{err}</p>}
              <div className="sf-admin-reply-actions">
                <button onClick={() => setReplyOpen(false)} className="sf-admin-reply-cancel">Cancel</button>
                <button onClick={submitReply} disabled={posting} className="sf-admin-reply-post">
                  {posting ? <Loader2 size={14} className="spinning" /> : <Send size={14} />}
                  Post Reply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ total: 0, average: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
  const [loading, setLoading] = useState(true);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formStars, setFormStars] = useState(0);
  const [formComment, setFormComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadReviews();
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    // Admin token can be passed as ?admin=TOKEN in URL or stored from previous visit
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('admin');
    const stored = localStorage.getItem('sf_admin_token');
    const token = urlToken || stored;
    if (!token) return;
    try {
      const r = await axios.post(`${API}/reviews/admin/verify`, { admin_token: token });
      if (r.data.valid) {
        localStorage.setItem('sf_admin_token', token);
        setIsAdmin(true);
      }
    } catch {
      // silent
    }
  };

  const loadReviews = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reviews`);
      setReviews(r.data.reviews || []);
      setSummary(r.data.summary || summary);
    } catch (e) {
      console.error('Could not load reviews', e);
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!formName.trim() || !formEmail.trim() || !formComment.trim() || formStars === 0) {
      setSubmitError('Please fill out every field, including a star rating.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        name: formName.trim(),
        email: formEmail.trim(),
        stars: formStars,
        comment: formComment.trim(),
      });
      setSubmitted(true);
      setFormName('');
      setFormEmail('');
      setFormStars(0);
      setFormComment('');
      loadReviews();
    } catch (e) {
      setSubmitError(e.response?.data?.detail || 'Could not submit review. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const totalReviews = summary.total || 0;
  const avg = summary.average || 0;

  return (
    <section id="reviews" className="sf-section sf-reviews" data-testid="reviews-section">
      <div className="sf-section-inner">
        <div className="sf-reviews-head">
          <div className="sf-section-eyebrow">
            <MessageSquare size={14} /> Your Voice Matters
          </div>
          <h2 className="sf-section-title">Rate us. Tell us your truth.</h2>
          <p className="sf-section-sub">
            Constructive feedback — positive or negative — helps us serve your artistic talent better.
            Every voice shapes what Scribe Flow becomes.
          </p>

          {totalReviews > 0 && (
            <div className="sf-reviews-summary" data-testid="reviews-summary">
              <div className="sf-rev-summary-score">
                <span className="sf-rev-avg">{avg.toFixed(1)}</span>
                <StarRow value={Math.round(avg)} readonly size={20} />
                <span className="sf-rev-count">from {totalReviews} {totalReviews === 1 ? 'voice' : 'voices'}</span>
              </div>
            </div>
          )}
        </div>

        <div className="sf-reviews-grid">
          {/* Submit form */}
          <div className="sf-review-form-card">
            <h3>Share your experience</h3>
            {submitted ? (
              <div className="sf-review-submitted" data-testid="review-submitted">
                <CheckCircle size={28} />
                <h4>Your voice has been heard.</h4>
                <p>Thank you for taking the time. Every word shapes what we build next.</p>
                <button onClick={() => setSubmitted(false)} className="sf-cta-btn sf-cta-btn-ghost">
                  Leave another review
                </button>
              </div>
            ) : (
              <form className="sf-review-form" onSubmit={submitReview}>
                <div className="sf-review-field">
                  <label>Your rating</label>
                  <StarRow value={formStars} onChange={setFormStars} size={28} />
                </div>
                <div className="sf-review-field">
                  <label>Your name</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="What should we call you?"
                    required
                    maxLength={60}
                    data-testid="review-name"
                  />
                </div>
                <div className="sf-review-field">
                  <label>Email <span className="sf-field-note">(never displayed publicly)</span></label>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="review-email"
                  />
                </div>
                <div className="sf-review-field">
                  <label>Your review</label>
                  <textarea
                    value={formComment}
                    onChange={(e) => setFormComment(e.target.value)}
                    placeholder="What worked? What could be better? Speak freely — your voice helps us grow."
                    rows={5}
                    required
                    minLength={10}
                    maxLength={2000}
                    data-testid="review-comment"
                  />
                  <div className="sf-review-charcount">
                    {formComment.length} / 2000
                  </div>
                </div>
                {submitError && (
                  <div className="sf-review-error">{submitError}</div>
                )}
                <button
                  type="submit"
                  className="sf-cta-btn sf-cta-btn-primary"
                  disabled={submitting}
                  data-testid="review-submit"
                >
                  {submitting ? (
                    <><Loader2 className="spinning" size={16} /> Sending...</>
                  ) : (
                    <><Send size={16} /> Share Your Voice</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Reviews feed */}
          <div className="sf-reviews-feed">
            <div className="sf-reviews-feed-header">
              <h3>What creators are saying</h3>
              {isAdmin && <span className="sf-admin-badge">⚡ Admin mode</span>}
            </div>
            {loading ? (
              <div className="sf-reviews-loading">
                <Loader2 className="spinning" size={20} /> Loading voices...
              </div>
            ) : reviews.length === 0 ? (
              <div className="sf-reviews-empty">
                <Sparkles size={28} />
                <p>Be the first to share your voice. Your review could be the one that helps the next creator step in.</p>
              </div>
            ) : (
              <div className="sf-reviews-list">
                {reviews.map((rev) => (
                  <ReviewCard
                    key={rev.id}
                    review={rev}
                    isAdmin={isAdmin}
                    onReplyPosted={loadReviews}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
